import type { EditorSDK } from '@wix/platform-editor-sdk';

import type { RouteConfiguration } from '../../../../types/controllers';
import { shouldEnableRouteCompression } from '../../../../utils/experiments';
import { log, logError } from '../../../../utils/monitoring';
import { APP_TOKEN } from '../../../constants';
import { decodeRoutes, encodeRoutes, isCompressedData } from './routes-compression';

const ROUTES_KEY = 'routes';

type PublicAppData = {
  routes?: RouteConfiguration[] | any; // Can be compressed or uncompressed
};

const logEmptyRoutesOverride = (routes: RouteConfiguration[]) => {
  try {
    if (!routes || !routes.length) {
      throw new Error('MAV2 (setRoutesInAppData): Routes data is empty');
    }
  } catch (e) {
    logError(e as Error);
  }
};

export const getRoutesFromAppData = async (editorSDK: EditorSDK): Promise<RouteConfiguration[] | null> => {
  let routes: RouteConfiguration[] | null = null;

  try {
    const publicData: PublicAppData = await editorSDK.document.tpa.data.getAppPublicData(APP_TOKEN, {
      key: ROUTES_KEY,
    });

    if (!publicData.routes) {
      return null;
    }

    if (isCompressedData(publicData.routes)) {
      routes = decodeRoutes(publicData.routes);
    } else {
      routes = publicData.routes;
    }
  } catch (error) {
    console.warn('Failed to get routes from app data:', error);
  }

  return routes;
};

export const setRoutesInAppData = async (editorSDK: EditorSDK, routes: RouteConfiguration[]) => {
  logEmptyRoutesOverride(routes);

  try {
    let value: any = routes;

    if (await shouldEnableRouteCompression()) {
      value = encodeRoutes(routes, { log });
    }

    // In case of changes to the public app data, make sure to update the routes in the global controller config on the viewer side
    return await editorSDK.document.tpa.data.setAppPublicData(APP_TOKEN, {
      key: ROUTES_KEY,
      value,
    });
  } catch (error) {
    console.warn('Failed to set routes in app data:', error);
  }
};
